import * as Service from 'types/service';
import { ConnectDragSource } from 'react-dnd';
import { FC } from 'react';
import { partyCardCssDefs } from 'components/service/party/partyRedesignCard/partyCardCssDefs';

import { Box } from '@tocktix/pantry';
import PartyRedesignBody from 'components/service/party/PartyRedesignBody';
import PartyRedesignStatusIndicator, {
  PartyRedesignStatusIndicatorProps,
} from 'components/service/party/PartyRedesignStatusIndicator';
import PartyStatusBar from './PartyStatusBar';
import PartyStatusIconSelector from 'components/settings/business-defined-states/PartyStatusIconSelector';

export interface PartyRedesignCardViewProps {
  party: Service.Party;
  isSelected: boolean;
  isDragging: boolean;
  isCompactView: boolean;
  isInteractive?: boolean;
  dragRef?: ConnectDragSource;
  disableStatusSelector?: boolean;
  unassignedTreatment: PartyRedesignStatusIndicatorProps['unassignedTreatment'];
  assignedTreatment: PartyRedesignStatusIndicatorProps['assignedTreatment'];
  isPartyStatusRefreshEnabled: boolean;
  onBodyClick?(): void;
  onStatusIndicatorClick?(): void;
  onContextMenu?(event: React.MouseEvent): void;
  onUpdatePartyState?(party: Service.Party, state: string, secondaryState: string | null): void;
}

/**
 * Purely presentational rendering of a redesigned party card, shared between
 * the live guest-list card and non-interactive previews (e.g. settings modals).
 */
const PartyRedesignCardView: FC<PartyRedesignCardViewProps> = ({
  party,
  party: {
    stateDescription: { stateName, primaryState },
  },
  isSelected,
  isDragging,
  isCompactView,
  isInteractive = true,
  dragRef,
  disableStatusSelector = false,
  unassignedTreatment,
  assignedTreatment,
  isPartyStatusRefreshEnabled,
  onBodyClick,
  onStatusIndicatorClick,
  onContextMenu,
  onUpdatePartyState,
}) => (
  <Box
    css={partyCardCssDefs.partyCard({
      isSelected,
      isDragging,
      isCompactView,
      isInteractive,
      isPartyStatusRefreshEnabled,
    })}
    onContextMenu={onContextMenu}
    data-testid="party-card-parent"
  >
    {isPartyStatusRefreshEnabled ? (
      <PartyStatusIconSelector
        party={party}
        handleUpdatePartyState={onUpdatePartyState}
        disabled={disableStatusSelector}
        position="auto"
      />
    ) : (
      <PartyStatusBar
        stateName={stateName ?? ''}
        primaryState={primaryState}
        isEligibleForHoldCharge={!!party.purchase?.isEligibleForBookingPolicyFee}
      />
    )}
    <PartyRedesignBody isSelected={isSelected} onClick={onBodyClick} party={party} />
    <PartyRedesignStatusIndicator
      dragRef={dragRef}
      /* eslint-disable-next-line @typescript-eslint/no-empty-function */
      onClick={onStatusIndicatorClick ?? (() => {})}
      party={party}
      unassignedTreatment={unassignedTreatment}
      assignedTreatment={assignedTreatment}
      isInteractive={isInteractive}
    />
  </Box>
);

export default PartyRedesignCardView;
